<script setup>
import { computed, h, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { picoSearch } from '@scmmishra/pico-search';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import SkillCard from 'dashboard/components-next/captain/pageComponents/skills/SkillCard.vue';
import SuggestedScenarios from 'dashboard/components-next/captain/assistant/SuggestedRules.vue';
import ScenariosCard from 'dashboard/components-next/captain/assistant/ScenariosCard.vue';
import BulkSelectBar from 'dashboard/components-next/captain/assistant/BulkSelectBar.vue';
import AddNewScenariosDialog from 'dashboard/components-next/captain/assistant/AddNewScenariosDialog.vue';
import AssistantPlayground from 'dashboard/components-next/captain/assistant/AssistantPlayground.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const { formatMessage } = useMessageFormatter();
const assistantId = computed(() => Number(route.params.assistantId));

const uiFlags = useMapGetter('captainScenarios/getUIFlags');
const assistantUiFlags = useMapGetter('captainAssistants/getUIFlags');
// The assistant carries the enabled tools, so the body stays behind the
// spinner until it lands. Otherwise a toggle would PATCH a config it never read.
const isFetching = computed(
  () => uiFlags.value.fetchingList || assistantUiFlags.value.fetchingItem
);
const scenarios = useMapGetter('captainScenarios/getRecords');
const tools = useMapGetter('captainTools/getRecords');
const getIntegration = useMapGetter('integrations/getIntegration');

const assistant = computed(
  () => store.getters['captainAssistants/getRecord'](assistantId.value) || {}
);

const searchQuery = ref('');

// tools.yml ships icon names from the backend icon set, the dashboard renders
// lucide classes.
const TOOL_ICONS = {
  'note-add': 'i-lucide-notebook-pen',
  'eye-off': 'i-lucide-eye-off',
  'exclamation-triangle': 'i-lucide-triangle-alert',
  tag: 'i-lucide-tag',
  search: 'i-lucide-search',
  checkmark: 'i-lucide-circle-check',
  'user-switch': 'i-lucide-arrow-right-left',
};

// Skills that ride on an integration. They stay locked until the account
// connects the app, and the card sends the admin to that integration page.
const INTEGRATION_SKILLS = [
  {
    id: 'shopify',
    icon: 'i-lucide-shopping-bag',
    routeName: 'settings_integrations_shopify',
  },
  {
    id: 'linear',
    icon: 'i-lucide-square-kanban',
    routeName: 'settings_integrations_linear',
  },
  {
    id: 'notion',
    icon: 'i-lucide-book-open',
    routeName: 'settings_integrations_notion',
  },
  {
    id: 'slack',
    icon: 'i-lucide-hash',
    routeName: 'settings_integrations_slack',
  },
];

// faq_lookup and handoff are wired into the assistant prompt itself, so they are
// not switchable here — only the tools a scenario reaches for are.
const CORE_TOOL_IDS = ['faq_lookup', 'handoff'];

const builtInTools = computed(() =>
  tools.value.filter(tool => !tool.custom && !CORE_TOOL_IDS.includes(tool.id))
);

// Toggles land faster than the PATCH round trip, so the pending list drives the
// switches while a save is in flight and successive flips build on each other.
const pendingToolIds = ref(null);

const enabledToolIds = computed(
  () =>
    pendingToolIds.value ??
    assistant.value.config?.enabled_tools ??
    builtInTools.value.map(tool => tool.id)
);

const lockedSkills = computed(() =>
  INTEGRATION_SKILLS.filter(
    skill => !getIntegration.value(skill.id, { hooks: [] }).hooks?.length
  ).map(skill => ({
    ...skill,
    title: t(
      `CAPTAIN.ASSISTANTS.SKILLS.LOCKED.APPS.${skill.id.toUpperCase()}.TITLE`
    ),
    description: t(
      `CAPTAIN.ASSISTANTS.SKILLS.LOCKED.APPS.${skill.id.toUpperCase()}.DESCRIPTION`
    ),
  }))
);

const toggleTool = async (toolId, isEnabled) => {
  const updatedTools = isEnabled
    ? [...enabledToolIds.value, toolId]
    : enabledToolIds.value.filter(id => id !== toolId);
  pendingToolIds.value = updatedTools;

  try {
    await store.dispatch('captainAssistants/update', {
      id: assistantId.value,
      config: {
        ...assistant.value.config,
        // The core tools stay in the list so scenarios keep resolving them.
        enabled_tools: [...updatedTools, ...CORE_TOOL_IDS],
      },
    });
  } catch (error) {
    const errorMessage =
      error?.message || t('CAPTAIN.ASSISTANTS.SKILLS.BUILT_IN.UPDATE_ERROR');
    useAlert(errorMessage);
  } finally {
    pendingToolIds.value = null;
  }
};

const openIntegration = routeName =>
  router.push({
    name: routeName,
    params: { accountId: route.params.accountId },
  });

const LINK_INSTRUCTION_CLASS =
  '[&_a[href^="tool://"]]:text-n-iris-11 [&_a:not([href^="tool://"])]:text-n-slate-12 [&_a]:pointer-events-none [&_a]:cursor-default';

const renderInstruction = instruction => () =>
  h('span', {
    class: `text-sm text-n-slate-12 py-4 prose prose-sm min-w-0 break-words ${LINK_INSTRUCTION_CLASS}`,
    innerHTML: instruction,
  });

// Suggested example scenarios for quick add
const scenariosExample = [
  {
    id: 1,
    title: 'Prospective Buyer',
    description:
      'Handle customers who are showing interest in purchasing a license',
    instruction:
      'If someone is interested in purchasing a license, ask them for following:\n\n1. How many licenses are they willing to purchase?\n2. Are they migrating from another platform?\n. Once these details are collected, do the following steps\n1. add a private note to with the information you collected using [Add Private Note](tool://add_private_note)\n2. Add label "sales" to the contact using [Add Label to Conversation](tool://add_label_to_conversation)\n3. Reply saying "one of us will reach out soon" and provide an estimated timeline for the response and [Handoff to Human](tool://handoff)',
    tools: ['add_private_note', 'add_label_to_conversation', 'handoff'],
  },
];

const filteredScenarios = computed(() => {
  const query = searchQuery.value.trim();
  const source = scenarios.value;
  if (!query) return source;
  return picoSearch(source, query, ['title', 'description', 'instruction']);
});

const shouldShowSuggestedRules = computed(() => {
  return uiSettings.value?.show_scenarios_suggestions !== false;
});

const closeSuggestedRules = () => {
  updateUISettings({ show_scenarios_suggestions: false });
};

// Bulk selection & hover state
const bulkSelectedIds = ref(new Set());
const hoveredCard = ref(null);

const handleRuleSelect = id => {
  const selected = new Set(bulkSelectedIds.value);
  selected[selected.has(id) ? 'delete' : 'add'](id);
  bulkSelectedIds.value = selected;
};

const buildSelectedCountLabel = computed(() => {
  const count = scenarios.value.length || 0;
  const isAllSelected = bulkSelectedIds.value.size === count && count > 0;
  return isAllSelected
    ? t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.UNSELECT_ALL', { count })
    : t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.SELECT_ALL', { count });
});

const selectedCountLabel = computed(() => {
  return t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const handleRuleHover = (isHovered, id) => {
  hoveredCard.value = isHovered ? id : null;
};

const getToolsFromInstruction = instruction => [
  ...new Set(
    [...(instruction?.matchAll(/\(tool:\/\/([^)]+)\)/g) ?? [])].map(m => m[1])
  ),
];

const updateScenario = async scenario => {
  try {
    await store.dispatch('captainScenarios/update', {
      id: scenario.id,
      assistantId: assistantId.value,
      ...scenario,
      tools: getToolsFromInstruction(scenario.instruction),
    });
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.UPDATE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.UPDATE.ERROR');
    useAlert(errorMessage);
  }
};

const deleteScenario = async id => {
  try {
    await store.dispatch('captainScenarios/delete', {
      id,
      assistantId: assistantId.value,
    });
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.DELETE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.DELETE.ERROR');
    useAlert(errorMessage);
  }
};

// TODO: Add bulk delete endpoint
const bulkDeleteScenarios = async ids => {
  const idsArray = ids || Array.from(bulkSelectedIds.value);
  await Promise.all(
    idsArray.map(id =>
      store.dispatch('captainScenarios/delete', {
        id,
        assistantId: assistantId.value,
      })
    )
  );
  bulkSelectedIds.value = new Set();
  useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.DELETE.SUCCESS'));
};

const addScenario = async scenario => {
  try {
    await store.dispatch('captainScenarios/create', {
      assistantId: assistantId.value,
      ...scenario,
      tools: getToolsFromInstruction(scenario.instruction),
    });
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.ERROR');
    useAlert(errorMessage);
  }
};

const addAllExampleScenarios = async () => {
  try {
    scenariosExample.forEach(async scenario => {
      await store.dispatch('captainScenarios/create', {
        assistantId: assistantId.value,
        ...scenario,
      });
    });
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.ERROR');
    useAlert(errorMessage);
  }
};

onMounted(() => {
  store.dispatch('captainAssistants/show', assistantId.value);
  store.dispatch('captainScenarios/get', {
    assistantId: assistantId.value,
  });
  store.dispatch('captainTools/getTools');
  store.dispatch('integrations/get');
});
</script>

<template>
  <PageLayout
    :header-title="$t('CAPTAIN.ASSISTANTS.SKILLS.TITLE')"
    :is-fetching="isFetching"
    :show-know-more="false"
    :show-pagination-footer="false"
    class="[&>header>div]:max-w-[80rem] [&>main>div]:max-w-[80rem]"
  >
    <template #body>
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 pb-8">
        <div class="flex flex-col gap-10">
          <div class="flex flex-col gap-4">
            <SettingsHeader
              :heading="$t('CAPTAIN.ASSISTANTS.SKILLS.BUILT_IN.TITLE')"
              :description="
                $t('CAPTAIN.ASSISTANTS.SKILLS.BUILT_IN.DESCRIPTION')
              "
            />
            <div class="flex flex-col gap-2">
              <SkillCard
                v-for="tool in builtInTools"
                :key="tool.id"
                :title="tool.title"
                :description="tool.description"
                :icon="TOOL_ICONS[tool.icon] || 'i-lucide-wrench'"
                :enabled="enabledToolIds.includes(tool.id)"
                @update:enabled="value => toggleTool(tool.id, value)"
              />
            </div>
          </div>

          <div class="flex flex-col gap-4">
            <SettingsHeader
              :heading="$t('CAPTAIN.ASSISTANTS.SKILLS.YOUR_SKILLS.TITLE')"
              :description="
                $t('CAPTAIN.ASSISTANTS.SKILLS.YOUR_SKILLS.DESCRIPTION')
              "
            />
            <div v-if="shouldShowSuggestedRules" class="flex flex-col gap-4">
              <SuggestedScenarios
                :title="$t('CAPTAIN.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.TITLE')"
                :items="scenariosExample"
                @close="closeSuggestedRules"
                @add="addAllExampleScenarios"
              >
                <template #default="{ item }">
                  <div class="flex items-center gap-3 justify-between">
                    <span class="text-sm text-n-slate-12">
                      {{ item.title }}
                    </span>
                    <Button
                      :label="
                        $t(
                          'CAPTAIN.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.ADD_SINGLE'
                        )
                      "
                      ghost
                      xs
                      slate
                      class="!text-sm !text-n-slate-11 flex-shrink-0"
                      @click="addScenario(item)"
                    />
                  </div>
                  <div class="flex flex-col">
                    <span class="text-sm text-n-slate-11 mt-2">
                      {{ item.description }}
                    </span>
                    <component
                      :is="
                        renderInstruction(
                          formatMessage(item.instruction, false)
                        )
                      "
                    />
                    <span class="text-sm text-n-slate-11 font-medium mb-1">
                      {{
                        t(
                          'CAPTAIN.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.TOOLS_USED'
                        )
                      }}
                      {{ item.tools?.map(tool => `@${tool}`).join(', ') }}
                    </span>
                  </div>
                </template>
              </SuggestedScenarios>
            </div>
            <div class="flex justify-between items-center gap-3">
              <BulkSelectBar
                v-model="bulkSelectedIds"
                :all-items="scenarios"
                :select-all-label="buildSelectedCountLabel"
                :selected-count-label="selectedCountLabel"
                :delete-label="
                  $t(
                    'CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.BULK_DELETE_BUTTON'
                  )
                "
                @bulk-delete="bulkDeleteScenarios"
              >
                <template #default-actions>
                  <AddNewScenariosDialog @add="addScenario" />
                </template>
              </BulkSelectBar>
              <div
                v-if="scenarios.length && bulkSelectedIds.size === 0"
                class="max-w-[18rem] w-full min-w-0"
              >
                <Input
                  v-model="searchQuery"
                  :placeholder="
                    t('CAPTAIN.ASSISTANTS.SCENARIOS.LIST.SEARCH_PLACEHOLDER')
                  "
                />
              </div>
            </div>
            <div v-if="scenarios.length === 0">
              <span class="text-n-slate-11 text-sm">
                {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.EMPTY_MESSAGE') }}
              </span>
            </div>
            <div v-else-if="filteredScenarios.length === 0">
              <span class="text-n-slate-11 text-sm">
                {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.SEARCH_EMPTY_MESSAGE') }}
              </span>
            </div>
            <div v-else class="flex flex-col gap-2">
              <ScenariosCard
                v-for="scenario in filteredScenarios"
                :id="scenario.id"
                :key="scenario.id"
                :title="scenario.title"
                :description="scenario.description"
                :instruction="scenario.instruction"
                :tools="scenario.tools"
                :is-selected="bulkSelectedIds.has(scenario.id)"
                :selectable="
                  hoveredCard === scenario.id || bulkSelectedIds.size > 0
                "
                @select="handleRuleSelect"
                @delete="deleteScenario(scenario.id)"
                @update="updateScenario"
                @hover="isHovered => handleRuleHover(isHovered, scenario.id)"
              />
            </div>
          </div>

          <div v-if="lockedSkills.length" class="flex flex-col gap-4">
            <SettingsHeader
              :heading="$t('CAPTAIN.ASSISTANTS.SKILLS.LOCKED.TITLE')"
              :description="$t('CAPTAIN.ASSISTANTS.SKILLS.LOCKED.DESCRIPTION')"
            />
            <div class="flex flex-col gap-2">
              <SkillCard
                v-for="skill in lockedSkills"
                :key="skill.id"
                :title="skill.title"
                :description="skill.description"
                :icon="skill.icon"
                locked
                :locked-label="$t('CAPTAIN.ASSISTANTS.SKILLS.LOCKED.CONNECT')"
                @connect="openIntegration(skill.routeName)"
              />
            </div>
          </div>
        </div>

        <!-- The preview sits beside the skills so a toggle or a new scenario
             can be tried without leaving the tab. -->
        <div class="flex flex-col gap-4 lg:sticky lg:top-4 lg:self-start">
          <SettingsHeader
            :heading="$t('CAPTAIN.ASSISTANTS.SKILLS.PREVIEW.TITLE')"
            :description="$t('CAPTAIN.ASSISTANTS.SKILLS.PREVIEW.DESCRIPTION')"
          />
          <AssistantPlayground
            :assistant-id="assistantId"
            class="sticky top-4 border rounded-xl bg-n-solid-1 border-n-weak h-[32rem]"
          />
        </div>
      </div>
    </template>
  </PageLayout>
</template>
